import React, { useEffect, useMemo, useState } from 'react'
import {
  CCard,
  CCardBody,
  CFormInput,
  CFormSelect,
  CInputGroup,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilSearch } from '@coreui/icons'
import UserService from 'src/service/UserService'
import './UserLeaderboard.css'

const columns = [
  { label: 'Rank', key: 'team_rank', filter: false, _style: { width: '8%' } },
  { label: 'Full Name', key: 'full_name', filter: false },
  { label: 'Team Name', key: 'my_team_name', filter: false },
  { label: 'Points', key: 'team_points', filter: false, _style: { width: '12%' } },
]

// The API already returns the teams in rank order; mirror that in the header state.
const defaultSorter = { column: 'team_rank', state: 'asc' }

const UserLeaderboard = () => {
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [roundList, setRoundList] = useState([])
  const [roundId, setRoundId] = useState(0)
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    setLoading(true)
    UserService.getRank(roundId)
      .then((result) => {
        setUsers(result.data?.user_teams ?? [])
        setRoundList(result.data?.round_list ?? [])
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [roundId])

  // full_name arrives nested under userdata, and points/rank as strings — flatten and coerce
  // them so the table can sort and search on the same values it renders.
  const rows = useMemo(
    () =>
      (users || []).map((item) => ({
        ...item,
        full_name: item?.userdata?.full_name || '',
        my_team_name: item?.my_team_name || '',
        team_points: item?.team_points === null ? null : Number(item?.team_points),
        team_rank: item?.team_rank === null ? null : Number(item?.team_rank),
      })),
    [users],
  )

  const visibleRows = useMemo(() => {
    const term = searchText.trim().toLowerCase()
    if (!term) return rows
    return rows.filter(
      (item) =>
        item.full_name.toLowerCase().includes(term) ||
        item.my_team_name.toLowerCase().includes(term),
    )
  }, [rows, searchText])

  const roundLabel = roundId
    ? `Round ${roundList.find((item) => String(item.id) === String(roundId))?.round ?? ''}`.trim()
    : 'Overall standings'

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Fantasy Leaderboard</h1>
        <p className="ge-page-subtitle">
          Rank members by fantasy points, overall or round by round.
        </p>
      </div>

      <CCard className="lb-panel">
        <CCardBody>
          <div className="lb-panel-heading">
            <div>
              <h2 className="lb-panel-title">{roundLabel}</h2>
              <p className="lb-panel-note">
                {visibleRows.length} {visibleRows.length === 1 ? 'team' : 'teams'} ranked
              </p>
            </div>
          </div>

          <div className="ge-table-toolbar d-flex flex-wrap align-items-center gap-2 mb-3">
            <CFormSelect
              className="lb-round-select"
              aria-label="Select round"
              value={roundId}
              onChange={(e) => setRoundId(e.target.value)}
            >
              <option value={0}>Overall standings</option>
              {roundList.map((item) => (
                <option value={item.id} key={item.id}>
                  Round {item?.round}
                </option>
              ))}
            </CFormSelect>

            <CInputGroup className="ge-search ms-auto">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by name or team..."
                aria-label="Search leaderboard"
              />
            </CInputGroup>
          </div>

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              sorterValue={defaultSorter}
              noItemsLabel={
                searchText ? 'No teams match this search' : 'No teams have been ranked yet'
              }
              scopedColumns={{
                team_rank: (item) => (
                  <td>
                    <span
                      className={`lb-rank${
                        item.team_rank >= 1 && item.team_rank <= 3
                          ? ` lb-rank--${item.team_rank}`
                          : ''
                      }`}
                    >
                      {item.team_rank ?? '-'}
                    </span>
                  </td>
                ),
                full_name: (item) => <td className="fw-semibold">{item.full_name || '-'}</td>,
                my_team_name: (item) => <td>{item.my_team_name || '-'}</td>,
                team_points: (item) => <td className="lb-points">{item.team_points ?? '-'}</td>,
              }}
              items={visibleRows}
              itemsPerPage={10}
              itemsPerPageSelect
              loading={loading}
              pagination
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
            />
          </div>
        </CCardBody>
      </CCard>
    </>
  )
}

export default UserLeaderboard
